import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { Radio } from '@delmonicos/shared-design-system-react/src/components/radio/radio';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { WithStatePaginatedTable } from '@delmonicos/shared-design-system-react/src/components/with-state-paginated-table/with-state-paginated-table';
import { GetRolesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/roles/models/inputs/get-roles-paginated.model';
import { GatewayRoleModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/roles/models/output/gateway-role.model';
import { useGatewayCreateUserFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-create-user.form-validation';
import { GatewayCreateUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-create-user.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { TransformedPaginatedData } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transformed-paginated-data.model';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  CellContext,
  OnChangeFn,
  SortingState,
  createColumnHelper,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { StateEnum } from '@app/core/model/enum/state.enum';
import { RoleListDescriptionCell } from '@app/feature/role/role-list/role-list-description.cell';
import { RoleListNameCell } from '@app/feature/role/role-list/role-list-name.cell';
import { ROLE_LIST_SORT_FIELD } from '@app/feature/role/role-list/role-list.config';
import { useRoleListContext } from '@app/feature/role/role.context';
import { useCpmsBoGetRolesPaginatedQuery } from '@service/cpms-bo-role.service';

import * as css from './user-create-form-step-2.module.scss';

export interface UserCreateFormRoleSelectProps
  extends CellContext<GatewayRoleModel, unknown> {}

export const UserCreateFormRoleSelect = (
  props: UserCreateFormRoleSelectProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { row } = props;
  const { control } = useFormContext<GatewayCreateUserModel>();
  const { roleId } = useGatewayCreateUserFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Controller
      name="roleId"
      control={control}
      rules={roleId}
      render={({ field }) => {
        return (
          <Radio
            id={row.original.id}
            label=""
            checked={field.value === row.original.id}
            onChange={() => {
              field.onChange(row.original.id);
            }}
          />
        );
      }}
    />
  );
};

// /////////////////////////////////////////////////////////////////////////////

const columnHelper = createColumnHelper<GatewayRoleModel>();

const STEP2_ROLE_COLUMN_ID_LIST = ['select', 'name', 'description'];
const STEP2_ROLE_COLUMN_ORDER = [...STEP2_ROLE_COLUMN_ID_LIST];
const STEP2_ROLE_COLUMN_VISIBILITY = STEP2_ROLE_COLUMN_ID_LIST.reduce(
  (acc, id) => ({ ...acc, [id]: true }),
  {},
);

export interface UserCreateFormStep2ViewProps {
  paginationLimit: number;
  paginationLimitList: number[];
  paginationState: StateEnum;
  paginationSorting: SortingState;
  resource?: TransformedPaginatedData<GatewayRoleModel>;
  onPaginationLimitChange: (paginationLimitNext: number) => void;
  onPaginationPageChange: (paginationOffsetNext: number) => void;
  onPaginationSortingChange?: OnChangeFn<SortingState>;
}

export const UserCreateFormStep2View = ({
  paginationLimit,
  paginationLimitList,
  paginationState,
  paginationSorting,
  resource,
  onPaginationLimitChange,
  onPaginationPageChange,
  onPaginationSortingChange,
}: UserCreateFormStep2ViewProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  // Nothing to do

  // LIST //////////////////////////////////////////////////////////////////////

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: '',
        cell: UserCreateFormRoleSelect,
      }),
      columnHelper.accessor('name', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.outputs-role-template.name'),
        cell: RoleListNameCell,
      }),
      columnHelper.accessor('description', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.outputs-role-template.description'),
        cell: RoleListDescriptionCell,
      }),
    ],
    [t],
  );

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <WithStatePaginatedTable
      paginatedData={resource}
      state={paginationState}
      sorting={paginationSorting}
      columns={columns}
      columnOrder={STEP2_ROLE_COLUMN_ORDER}
      columnVisibility={STEP2_ROLE_COLUMN_VISIBILITY}
      limit={paginationLimit}
      limitList={paginationLimitList}
      errorMessage={t('$.charge-point-list.error')}
      onLimitChange={onPaginationLimitChange}
      onPageChange={onPaginationPageChange}
      onSortingChange={onPaginationSortingChange}
    />
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UserCreateFormStep2Controller = (): React.JSX.Element => {
  // CONTEXT /////////////////////////////////////////////////////////////////

  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
  } = useRoleListContext();

  // USER PERMISSIONS ////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS /////////////////////////////////////////////////////////////////

  // Nothing to do

  // DATA FETCHING ///////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetRolesPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // PAGE TITLE //////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS ////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE BREADCRUMB /////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS ////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? ROLE_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetRolesPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: ChargePointListViewProtected } = withProtectedResource(
    UserCreateFormStep2View,
    queryState,
    {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.ROLES,
          action: ActionsByResource.ROLES.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    },
  )({
    paginationLimit: getPaginatedQueryParameters.limit,
    paginationLimitList: getPaginatedLimitList,
    paginationState,
    paginationSorting: [
      {
        desc: getPaginatedQueryParameters.sortOrder === -1,
        id: getPaginatedQueryParameters.sortField ?? ROLE_LIST_SORT_FIELD,
      },
    ],
    onPaginationLimitChange: handlePaginationLimitChange,
    onPaginationPageChange: handlePaginationPageChange,
    onPaginationSortingChange: handlePaginationSortingChange,
  });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      <Stack className={css.tableContainer}>
        {ChargePointListViewProtected}
      </Stack>
    </Stack>
  );
};
